import { Component, OnInit, AfterViewInit, EventEmitter, Output, SimpleChanges, OnChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Check, LucideAngularModule, SendHorizontal } from 'lucide-angular';
import { HeaderComponent } from '../header/header.component';
import { MessageComponent } from './message/message.component';
import { BaseComponent } from '../base.component';
import { Chat } from '~/app/types/chat';
import { Assistant } from '~/app/types/assistant';
import { Message } from '~/app/types/message';
import { ChatService } from '~/app/services/chat.service';
import { AssistantService } from '~/app/services/assistant/assistant.user.service';
import { ChatHeaderComponent } from './header/header.component';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { StarRatingComponent } from '../star-rating/star-rating.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { DataSource, CollectionViewer } from '@angular/cdk/collections';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-chat',
  standalone: true,
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LucideAngularModule,
    HeaderComponent,
    MessageComponent,
    ChatHeaderComponent,
    HlmButtonDirective,
    StarRatingComponent,
    ScrollingModule
  ]
})
export class ChatComponent extends BaseComponent implements OnInit, AfterViewInit, OnChanges {
  ds!: ChatDataSource;

  // lucide icons
  readonly SendIcon = SendHorizontal;
  readonly checkIcon = Check;

  activeChats: Chat[] = [];
  currentChat!: Chat;
  chatAvailable: boolean = false;
  messageInput = new FormControl({ value: '', disabled: !this.chatAvailable || !this.currentChat.assistant.id });
  assistants: Assistant[] = [];
  waitingForResponse: boolean = false;
  waitingForRating = true;

  ratingSubmitted = false;

  loadingMessage: Message = {
    role: 'bot',
    message: '...',
    timestamp: new Date()
  };

  showChoices: boolean = false;
  selectedChoices: string[] = [];
  currentAssistant!: Assistant;
  messages: Message[] = []; // Added property to store messages
  startOfThread: boolean = false;

  @Output() choicesSubmitted = new EventEmitter<string[]>();

  constructor(
    private chatService: ChatService,
    private assistantService: AssistantService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.ds = new ChatDataSource(this.chatService);
    this.subscribeToObservables();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatAvailable'] || changes['currentChat']) {
      this.toggleMessageInput();
    }
  }

  async ngAfterViewInit(): Promise<void> {
    this.setupScrollListener();
  }

  subscribeToObservables(): void {
    console.log('subscribeToObservables called');
    this.subscribe(this.chatService.activeChats$, chats => {
      console.log('activeChats subscription triggered');
      this.activeChats = chats;
      // Update messages if currentChat is set
      if (this.currentChat) {
        const found = chats.find(chat => chat === this.currentChat);
        if (found) {
          // Sort messages by timestamp to ensure newest at the bottom
          this.messages = [...(found.messages || [])].sort((a, b) => {
            const timeA = new Date(a.timestamp).getTime();
            const timeB = new Date(b.timestamp).getTime();
            return timeA - timeB; // Ascending order - oldest to newest
          });
          console.log('messages updated:', this.messages);
          // Update choices visibility when messages change
          this.updateShowChoices();
          this.scrollToBottom(); // Ensure UI reflects the latest messages
        }
      }
    });

    this.subscribe(this.chatService.currentChat$, chat => {
      console.log('currentChat subscription triggered');
      this.updateCurrentChat(chat);
    });

    this.subscribe(this.chatService.waitingForResponse$, waitingForResponse => {
      console.log('waitingForResponse subscription triggered:', waitingForResponse);
      this.waitingForResponse = waitingForResponse;
    });

    this.subscribe(this.chatService.waitingForRating$, waitingForRating => {
      console.log('waitingForRating subscription triggered:', waitingForRating);
      this.waitingForRating = waitingForRating;
    });

    this.subscribe(this.assistantService.assistants$, assistants => {
      console.log('assistants subscription triggered');
      this.assistants = assistants;
    });

    this.subscribe(this.chatService.chatAvailable$, chatAvailable => {
      console.log('chatAvailable subscription triggered:', chatAvailable);
      this.chatAvailable = chatAvailable;
    });

    this.subscribe(this.chatService.startOfThread$, startOfThread => {
      console.log('startOfThread subscription triggered:', startOfThread);
      this.startOfThread = startOfThread;
    });
  }

  updateCurrentChat(chat: Chat): void {
    this.currentChat = chat;
    // Sort messages by timestamp to ensure newest at the bottom
    this.messages = [...(chat.messages || [])].sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeA - timeB; // Ascending order - oldest to newest
    });
    this.updateShowChoices();
    this.toggleMessageInput();
    this.scrollToBottom();
  }
  
  // Update showChoices based on the latest assistant message
  updateShowChoices(): void {
    // Only show choices if there's an assistant message with choices and no user response
    const msgWithChoices = this.getLatestAssistantMessageWithChoices();
    this.showChoices = !!msgWithChoices;
  }

  toggleMessageInput(): void {
    if (!this.chatAvailable || !this.currentChat.assistant.id) {
      this.messageInput.disable();
    } else {
      this.messageInput.enable();
    }
  }

  setupScrollListener(): void {
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      let debounceTimeout: any;
      let lastScrollTop = chatContainer.scrollTop;

      chatContainer.addEventListener('scroll', () => {
        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => {
          console.log('Scroll event triggered');
          const currentScrollTop = chatContainer.scrollTop;

          // Prevent duplicate calls by checking if scrollTop has changed and startOfThread is false
          if (currentScrollTop !== lastScrollTop && currentScrollTop === 0 && !this.startOfThread) {
            console.log('Loading more messages');
            this.chatService.loadMoreMessages();
          }

          lastScrollTop = currentScrollTop;
        }, 200); // Debounce delay of 200ms
      });
    }
  }

  scrollToBottom(): void {
    setTimeout(() => {
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 0);
  }

  toggleChoice(choice: string): void {
    const index = this.selectedChoices.indexOf(choice);
    if (index === -1) {
      this.selectedChoices.push(choice);
    } else {
      this.selectedChoices.splice(index, 1);
    }
  }

  getAssistantColors(assistantId: string): any {
    return this.assistantService.getAssistantColors(assistantId);
  }

  getCurrentChatData(): Chat | undefined {
    return this.activeChats.find(chat => chat === this.currentChat);
  }

  getLatestMessage(): Message | undefined {
    const chat = this.getCurrentChatData();
    if (!chat || chat.messages.length === 0) return;
    const latestMessage = chat.messages[chat.messages.length - 1];
    return latestMessage;
  }
  
  // Get the latest assistant message with choices that has no user response after it
  getLatestAssistantMessageWithChoices(): Message | undefined {
    const chat = this.getCurrentChatData();
    if (!chat || chat.messages.length === 0) return;
    
    // Start from the end and find the latest assistant message with choices
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      const msg = chat.messages[i];
      
      // Check if this is an assistant message with choices
      if (msg.role === 'bot' && msg.choices && msg.choices.length > 0) {
        // Check if any user message appears after this assistant message
        let userResponseFound = false;
        for (let j = i + 1; j < chat.messages.length; j++) {
          if (chat.messages[j].role === 'user') {
            userResponseFound = true;
            break;
          }
        }
        
        // If no user response, this is the message we want
        if (!userResponseFound) {
          return msg;
        }
        
        // If we found a user response, no need to continue as older messages
        // would have user responses after them too
        break;
      }
    }
    
    return undefined;
  }

  addChat(assistant: Assistant): void {
    this.chatService.addChat(assistant);
  }

  removeChat(chat: Chat): void {
    this.chatService.removeChat(chat);
  }

  setCurrentChat(chat: Chat): void {
    this.chatService.setCurrentChat(chat);
  }

  handleChoicesSubmitted(): void {
    this.choicesSubmitted.emit(this.selectedChoices);
  }

  async sendMessage() {
    const userInput = this.messageInput.value?.trim();
    if (!userInput || !this.chatAvailable) return;

    const userMessage: Message = {
      role: 'user',
      message: userInput,
      timestamp: new Date()
    };

    if (!this.currentChat) return;
    this.chatService.addMessage(this.currentChat, userMessage);
    this.messageInput.reset();
    this.resetTextareaHeight();

    // Update choices visibility - should hide after user sends a message
    this.showChoices = false;
    this.selectedChoices = [];

    this.chatService.sendMessageToAssistant(userInput);
  }

  resetTextareaHeight(): void {
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.style.height = 'inherit';
    }
  }

  toggleShowChoices(): void {
    this.showChoices = !this.showChoices;
    if (!this.showChoices) {
      this.selectedChoices = [];
    }
  }

  autoGrow(event: any): void {
    event.target.style.height = 'inherit';
    event.target.style.height = `${event.target.scrollHeight}px`;
  }

  handleEnterKey(event: any): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    } else if (event.key === 'Enter' && event.metaKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }
}

export class ChatDataSource extends DataSource<Message | undefined> {
  private _pageSize = 50;
  private _cachedData: (Message | undefined)[] = [];
  private _fetchedPages = new Set<number>();
  private readonly _dataStream = new BehaviorSubject<(Message | undefined)[]>(this._cachedData);
  private readonly _subscription = new Subscription();

  constructor(private chatService: ChatService) {
    super();
  }

  connect(collectionViewer: CollectionViewer): Observable<(Message | undefined)[]> {
    this._subscription.add(
      collectionViewer.viewChange.subscribe(range => {
        const startPage = this._getPageForIndex(range.start);
        const endPage = this._getPageForIndex(range.end - 1);
        for (let i = startPage; i <= endPage; i++) {
          this._fetchPage(i);
        }
      })
    );
    return this._dataStream;
  }

  disconnect(): void {
    this._subscription.unsubscribe();
  }

  private _getPageForIndex(index: number): number {
    return Math.floor(index / this._pageSize);
  }

  private _fetchPage(page: number): void {
    if (this._fetchedPages.has(page)) {
      return;
    }
    this._fetchedPages.add(page);

    const offset = page * this._pageSize;
    this.chatService.loadMoreMessages(offset, this._pageSize).then((messages: Message[]) => {
      this._cachedData.splice(
        offset,
        this._pageSize,
        ...messages
      );
      this._dataStream.next(this._cachedData);
    });
  }
}

